import { Children, createContext, Fragment, isValidElement, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { CHAT_REFERENCE_TYPES } from '@till-erp/chat/values';
import { ChatDiscuss, CHAT_DISCUSS_LABEL } from './chat/chat-discuss-button.tsx';
import type { RecordMenuItem } from './record-head-values.ts';
import { PAGE_HEAD_MAX_BUTTONS } from './ui/variants.ts';

/** Datensatz, den der Kopf „Im Chat besprechen“ anbietet (Story 24.8); `null` = keine Kopf-Aktion. */
export interface RecordChatTarget {
  entityType: string;
  entityId: string;
}

/** One contextual action in the existing header, shared by standard and bespoke record frames. */
export const RecordContextAction = createContext<RecordChatTarget | null>(null);

export function RecordContextActions({ entityType, entityId, children }: { entityType?: string | undefined; entityId?: string | undefined; children: ReactNode }) {
  const target = useMemo(() => (entityType && entityId && (CHAT_REFERENCE_TYPES as readonly string[]).includes(entityType) ? { entityType, entityId } : null), [entityType, entityId]);
  return <RecordContextAction.Provider value={target}>{children}</RecordContextAction.Provider>;
}

/**
 * Wie viele Knöpfe ein Knopf-Slot des Kopfs belegt: ein Fragment (z. B. „Senden“ + „Bearbeiten“ beim Angebot) zählt mit
 * seinen Kindern, leere Stellen (`null`, `false`) gar nicht.
 */
export function countHeadButtons(node: ReactNode): number {
  return Children.toArray(node).reduce<number>((sum, child) => sum + (isValidElement<{ children?: ReactNode }>(child) && child.type === Fragment ? countHeadButtons(child.props.children) : 1), 0);
}

/**
 * Story 24.8 (Entscheid Hauptsitzung): „Im Chat besprechen“ steht als Knopf, solange mit ihm höchstens 3 Knöpfe vor „⋯“
 * stehen (`taken` = schon belegte Plätze); sonst ist es der erste Eintrag im „⋯“ – keine Ausnahme von der Regel, die
 * Knöpfe der Seite haben Vorrang. Die Breite prüft zusätzlich `useHeadRowFit`.
 */
export function chatInlineInHead(taken: number): boolean {
  return taken < PAGE_HEAD_MAX_BUTTONS;
}

/** Eintrag „Im Chat besprechen“ für ein eigenes „⋯“ der Seite (`menuSlot`, z. B. das Auftragsmenü); `null` = keiner. */
export const RecordMenuLead = createContext<RecordMenuItem | null>(null);

/** Erster Eintrag eines eigenen „⋯“-Menüs: zeigt „Im Chat besprechen“, wenn der Kopf ihn ins Menü gelegt hat. */
export function RecordMenuLeadItem({ onSelect, className }: { onSelect: () => void; className: string }) {
  const lead = useContext(RecordMenuLead);
  if (!lead) return null;
  return (
    <button
      type="button"
      onClick={() => {
        onSelect();
        lead.onSelect();
      }}
      className={className}
    >
      {lead.label}
    </button>
  );
}

/**
 * „⋯“ mit „Im Chat besprechen“ als erstem Eintrag: `render` bekommt den Eintrag (oder `null`, wenn der Chat gesperrt ist)
 * und baut daraus das Menü. Die Auswahl der Unterhaltung hängt am „⋯“ (Popover, am Handy Blatt).
 */
export function ChatInMenu({ target, render }: { target: RecordChatTarget; render: (lead: RecordMenuItem | null) => ReactNode }) {
  return (
    <ChatDiscuss variant="menu" entityType={target.entityType} entityId={target.entityId}>
      {(open) => render(open ? { label: CHAT_DISCUSS_LABEL, onSelect: open } : null)}
    </ChatDiscuss>
  );
}

/** „⋯“ der Seite (eigenes Menü) mit „Im Chat besprechen“ als erstem Eintrag (`RecordMenuLeadItem` im Menü). */
export function ChatInMenuSlot({ target, children }: { target: RecordChatTarget; children: ReactNode }) {
  return <ChatInMenu target={target} render={(lead) => <RecordMenuLead.Provider value={lead}>{children}</RecordMenuLead.Provider>} />;
}

/** Sichtbarer Knopf „Im Chat besprechen“ im Kopf. */
export function ChatInHead({ target }: { target: RecordChatTarget }) {
  return <ChatDiscuss variant="button" entityType={target.entityType} entityId={target.entityId} />;
}

/**
 * Mehr Breite, als eine Bildlaufleiste ausmacht: erst dann probiert der Kopf den Knopf wieder neben dem Namen (verhindert
 * Hin- und Herspringen, wenn die Bildlaufleiste kommt und geht).
 */
const REFIT_SLACK_PX = 24;

/**
 * Story 24.8: Am PC und Tablet steht die Knopfreihe neben dem Namen, nie darunter (Entscheid Hauptsitzung). Rutscht sie
 * darunter, wandert zuerst „Im Chat besprechen“ ins „⋯“ (`chatToMenu`, nur wenn er als Knopf vorgesehen war); reicht auch
 * das nicht (z. B. Angebot: drei Knöpfe und „⋯“ in der schmalen Spalte), steht die Reihe rechtsbündig über dem Namen
 * (`above`, „Knöpfe oben rechts“). Gemessen vor dem Zeichnen (Layout-Effekt, kein Flackern) und bei Größenänderung
 * nachgeführt; erst bei deutlich mehr Breite wird neu probiert. `key` = Belegung der Reihe (ändert sie sich, neu messen).
 */
export function useHeadRowFit(
  enabled: boolean,
  key: string,
  chatCandidate: boolean,
  refs: { head: RefObject<HTMLElement | null>; name: RefObject<HTMLElement | null>; row: RefObject<HTMLElement | null> },
  phone = false,
): { chatToMenu: boolean; above: boolean } {
  const [fit, setFit] = useState<{ key: string; stage: 1 | 2; width: number } | null>(null);
  const stage = enabled && fit !== null && fit.key === key ? fit.stage : 0;
  const { head, name, row } = refs;
  const measure = useRef<() => void>(() => {});
  useLayoutEffect(() => {
    measure.current = () => {
      const h = head.current;
      const n = name.current;
      const r = row.current;
      if (!enabled || !h || !n || !r) return;
      const rowTop = r.getBoundingClientRect().top;
      const nameTop = n.getBoundingClientRect().top;
      // Handy (HS 03.11.2026, F2 = A): die Reihe steht dort bewusst unter dem Namen; bricht sie in eine zweite Zeile um,
      // rückt nur „Im Chat besprechen“ ins „⋯“ (keine Stufe „über dem Namen“).
      if (phone) {
        // Umbruch = ein Knopf beginnt unterhalb des ersten (verschieden hohe Knöpfe sind kein Umbruch).
        const boxes = [...r.children].map((el) => el.getBoundingClientRect()).filter((b) => b.height > 0);
        const wraps = boxes.length > 1 && boxes.some((b) => b.top >= boxes[0]!.bottom - 1);
        if (stage > 0 && fit && h.clientWidth > fit.width + REFIT_SLACK_PX) setFit(null);
        else if (stage === 0 && wraps && chatCandidate) setFit({ key, stage: 1, width: h.clientWidth });
        return;
      }
      // Deutlich breiter geworden, oder die Reihe „über dem Namen“ passt doch daneben: von vorn probieren.
      if (stage > 0 && fit && (h.clientWidth > fit.width + REFIT_SLACK_PX || (stage === 2 && rowTop >= nameTop - 1))) {
        setFit(null);
        return;
      }
      // Reihe steht unter statt neben dem Namen: nächste Stufe (der Layout-Effekt rendert vor dem Zeichnen neu).
      if (stage < 2 && rowTop > nameTop + 1) {
        setFit({ key, stage: stage === 0 && chatCandidate ? 1 : 2, width: stage === 0 || !fit ? h.clientWidth : fit.width });
      }
    };
    measure.current();
  });
  useEffect(() => {
    if (!enabled || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure.current());
    for (const el of [head.current, row.current]) if (el) observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, head, row]);
  return { chatToMenu: stage >= 1 && chatCandidate, above: stage === 2 && !phone };
}
